import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, Field } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';

interface OnlineSettings {
  available: boolean;
  mercadopago: { configured: boolean; accountLabel: string | null; withSignature: boolean; updatedAt: string | null };
  webhookUrl: string;
}

/** Cobros en línea con la cuenta de Mercado Pago de la iglesia (el dinero va directo a la iglesia). */
export function OnlinePaymentsCard({ churchId }: { churchId: string }) {
  const data = useAsync((signal) => api.get<OnlineSettings>(`/finance/churches/${churchId}/online-payments`, signal), [churchId]);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  if (data.status !== 'ready') return null;
  if (!data.data.available) return <Card title="Cobros en línea"><p className="muted small">Los cobros en línea aún no están disponibles en KEORA.</p></Card>;
  const s = data.data;
  const url = `/finance/churches/${churchId}/online-payments/mercadopago`;

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    setBusy(true);
    setMsg(null);
    try {
      await api.put(url, { accessToken: String(f.get('accessToken') ?? '').trim(), webhookSecret: String(f.get('webhookSecret') ?? '').trim() || null });
      setMsg({ tone: 'success', text: 'Listo: la iglesia ya puede cobrar en línea.' });
      setEditing(false);
      data.reload();
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm('¿Dejar de cobrar en línea? Se borran las claves guardadas.')) return;
    setBusy(true);
    try {
      await api.delete(url);
      setMsg({ tone: 'success', text: 'Se quitaron las claves de Mercado Pago.' });
      data.reload();
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Cobros en línea (Mercado Pago)">
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      <p className="muted small">
        Para cobrar en línea el campamento (y lo que venga después) con la cuenta de Mercado Pago <strong>de la iglesia</strong>. El dinero llega
        directo a esa cuenta; KEORA no lo toca. Cada pago aprobado se registra solo en Finanzas.
      </p>
      {s.mercadopago.configured && !editing ? (
        <>
          <p>
            Conectado a <strong>{s.mercadopago.accountLabel}</strong>
            {s.mercadopago.withSignature ? ' · avisos firmados' : ''}.
          </p>
          <div className="actions">
            <Button variant="secondary" onClick={() => setEditing(true)}>
              Cambiar claves
            </Button>
            <Button variant="ghost" busy={busy} onClick={() => void remove()}>
              Quitar
            </Button>
          </div>
        </>
      ) : (
        <form className="form" onSubmit={save} noValidate>
          <ol className="steps-list small">
            <li>
              Entra a <strong>mercadopago.com.mx/developers</strong> con la cuenta de la iglesia → Tus integraciones → crea una aplicación.
            </li>
            <li>
              En <strong>Credenciales de producción</strong> copia el <strong>Access Token</strong> (empieza con APP_USR-).
            </li>
            <li>
              Opcional: en <strong>Webhooks</strong> pega esta URL, elige el evento «Pagos» y copia la clave secreta:
              <br />
              <code className="code">{s.webhookUrl}</code>
            </li>
          </ol>
          <Field label="Access Token de producción" name="accessToken" type="password" autoComplete="off" required />
          <Field label="Clave secreta de Webhooks (opcional)" name="webhookSecret" type="password" autoComplete="off" />
          <p className="small muted">Las claves se guardan cifradas y nunca se vuelven a mostrar.</p>
          <div className="actions">
            <Button type="submit" busy={busy}>
              Guardar y comprobar
            </Button>
            {s.mercadopago.configured ? (
              <Button variant="ghost" onClick={() => setEditing(false)}>
                Cancelar
              </Button>
            ) : null}
          </div>
        </form>
      )}
    </Card>
  );
}
